import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import { Comment as CommentIcon, Delete, Replay } from "@mui/icons-material";
import { Grid2, IconButton } from "@mui/material";
import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import type { Duration } from "moment";
import "moment-duration-format";
import { Cobble } from "../../models/Cobble";

const moment = getMomentTz();

export const useTableData = ({
	setShowModal,
	onShowComment,
}: {
	setShowModal: (show: "Delete" | "Undelete" | "") => void;
	onShowComment: (
		show: boolean,
		cobble?: Cobble,
		position?: { top: number; left: number },
	) => void;
}) => {
	const columnDefs: (ColDef | ColGroupDef)[] = [
		{
			field: "Start",
			headerName: "Start",
			valueGetter: (params) =>
				dxtToLocalServerTime((params.data as Cobble)?.Start, "HH:mm:ss") || "",
			flex: 1,
			sortable: true,
		},
		{
			field: "End",
			headerName: "End",
			valueGetter: (params) =>
				dxtToLocalServerTime((params.data as Cobble)?.End, "HH:mm:ss") || "-",
			flex: 1,
			sortable: true,
		},
		{
			field: "Duration",
			headerName: "Duration",
			valueFormatter: (params) => {
				if (params.value) {
					return (moment.duration(params.value, "seconds") as Duration).format(
						"h[h] m[m] s[s]",
					);
				}
				return "";
			},
			flex: 1,
			sortable: true,
		},
		{
			field: "LastStandName",
			headerName: "Last Stand",
			flex: 1,
			sortable: true,
		},
		{
			field: "CorrectedAt",
			headerName: "Corrected At",
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					(params.data as Cobble)?.CorrectedAt,
					"MM/dd HH:mm:ss",
				) || "",
		},
		{
			field: "OperatorName",
			headerName: "Corrected By",
			flex: 1,
		},
		{
			field: "Comment",
			headerName: "Comments",
			maxWidth: 60,
			sortable: false,
			filter: false,
			cellRenderer: (params: { data: Cobble }) => {
				if (!params.data?.Comment) return null;
				return (
					<IconButton
						size="small"
						color="primary"
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
							onShowComment(true, params.data, {
								top: e.clientY,
								left: e.clientX,
							});
						}}
					>
						<CommentIcon fontSize="small" />
					</IconButton>
				);
			},
		},
		{
			field: "",
			headerName: "",
			flex: 1,
			maxWidth: 70,
			cellRenderer: (params: { data: Cobble }) => (
				<Grid2
					container
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						opacity: 0,
						transition: "opacity 0.1s ease-in-out",
					}}
					className={`buttons`}
				>
					{(params.data as Cobble).IsCobble ? (
						<Grid2
							size={{ xs: 12, md: 12 }}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<IconButton
								style={{ cursor: "pointer" }}
								title="Delete"
								onClick={() => setShowModal("Delete")}
							>
								<Delete fontSize="inherit" color="secondary" />
							</IconButton>
						</Grid2>
					) : (
						<Grid2
							size={{ xs: 12, md: 12 }}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<IconButton
								style={{ cursor: "pointer" }}
								title="Undelete"
								onClick={() => setShowModal("Undelete")}
							>
								<Replay fontSize="inherit" color="primary" />
							</IconButton>
						</Grid2>
					)}
				</Grid2>
			),
		},
	];

	return { columnDefs };
};
